import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { 
  Card, 
  CardBody, 
  CardHeader, 
  CardFooter,
  Button, 
  ButtonGroup, 
  Chip, 
  Image, 
  Modal, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter,
  useDisclosure,
  Pagination,
  Divider
} from "@heroui/react";
import { 
  FaArrowRight, 
  FaChevronRight, 
  FaChevronLeft,
  FaSearch,
  FaServer,
  FaShieldAlt,
  FaCloud,
  FaGlobe,
  FaCode
} from 'react-icons/fa';

const Portfolio = () => {
  // Reference for scroll into view on filter change
  const portfolioRef = useRef(null);
  const isInView = useInView(portfolioRef, { once: true, amount: 0.1 });
  
  // State for modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Categories with icons
  const categories = [
    { key: 'all', label: 'Wszystkie', icon: <FaSearch /> },
    { key: 'webapps', label: 'Aplikacje Webowe', icon: <FaGlobe /> },
    { key: 'infrastructure', label: 'Infrastruktura', icon: <FaServer /> },
    { key: 'security', label: 'Cyberbezpieczeństwo', icon: <FaShieldAlt /> },
    { key: 'cloud', label: 'Cloud', icon: <FaCloud /> },
    { key: 'software', label: 'Oprogramowanie', icon: <FaCode /> }
  ];

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Active category
  const [activeCategory, setActiveCategory] = useState('all');

  // Expanded project details
  //const [expandedProject, setExpandedProject] = useState(null);

  // Projects data with more details
  const projects = [
    {
      id: 1,
      title: 'Migracja infrastruktury do chmury',
      category: 'cloud',
      image: require('./assets/img/migracja.png'),
      thumbnail: require('./assets/img/migracja.png'),
      technologies: ['Azure', 'Kubernetes', 'Docker', 'Terraform'],
      description: 'Kompleksowa migracja infrastruktury klienta do Microsoft Azure z zapewnieniem ciągłości działania.',
      longDescription: 'Przeprowadziliśmy kompleksową migrację infrastruktury IT dużej firmy produkcyjnej do chmury Microsoft Azure. Projekt obejmował analizę istniejącej infrastruktury, zaprojektowanie architektury chmurowej, przygotowanie środowiska, migrację danych i aplikacji oraz testy. Cały proces został zrealizowany bez przestojów w działaniu systemów krytycznych.',
      duration: '8 miesięcy',
      completionDate: '2023-04',
      teamSize: 6,
      demoLink: null,
      caseStudyLink: '#',
      gallery: [
        require('./assets/img/migracja.png')
      ],
      achievements: [
        'Redukcja kosztów utrzymania infrastruktury o 40%',
        'Zwiększenie dostępności systemów do 99.99%',
        'Wdrożenie automatycznego skalowania zasobów'
      ]
    },
    {
      id: 2,
      title: 'Audyt bezpieczeństwa systemów',
      category: 'security',
      image: require('./assets/img/audyt.png'),
      thumbnail: require('./assets/img/audyt.png'),
      technologies: ['Nessus', 'Metasploit', 'Burp Suite', 'OWASP ZAP'],
      description: 'Kompleksowy audyt bezpieczeństwa systemów IT z raportem i rekomendacjami dla firmy.',
      longDescription: 'Przeprowadziliśmy kompleksowy audyt bezpieczeństwa systemów IT w firmie, obejmujący testy penetracyjne, analizę konfiguracji, ocenę bezpieczeństwa aplikacji oraz kontrolę polityk i procedur. Na podstawie wyników przygotowaliśmy szczegółowy raport z rekomendacjami oraz plan naprawczy, który następnie wdrożyliśmy.',
      duration: '3 miesiące',
      completionDate: '2023-08',
      teamSize: 3,
      demoLink: null,
      caseStudyLink: '#',
      gallery: [
        require('./assets/img/audyt.png')
      ],
      achievements: [
        'Wykrycie i usunięcie 12 krytycznych podatności',
        'Wdrożenie zaawansowanych systemów zabezpieczeń',
        'Zgodność z regulacjami KNF i normami ISO 27001'
      ]
    },
    {
      id: 3,
      title: 'Wdrożenie infrastruktury serwerowej',
      category: 'infrastructure',
      image: require('./assets/img/wdrozenie.png'),
      thumbnail: require('./assets/img/wdrozenie.png'),
      technologies: ['VMware', 'Dell PowerEdge', 'Cisco UCS', 'Windows Server'],
      description: 'Zaprojektowanie i wdrożenie zaawansowanej infrastruktury serwerowej dla centrum danych.',
      longDescription: 'Zaprojektowaliśmy i wdrożyliśmy zaawansowaną infrastrukturę serwerową dla centrum danych dużej firmy ubezpieczeniowej. Rozwiązanie obejmowało serwery, systemy pamięci masowej, infrastrukturę sieciową oraz oprogramowanie do wirtualizacji. Całość została skonfigurowana w architekturze wysokiej dostępności, z automatycznym odtwarzaniem po awarii.',
      duration: '5 miesięcy',
      completionDate: '2022-11',
      teamSize: 5,
      demoLink: null,
      caseStudyLink: '#',
      gallery: [
        require('./assets/img/wdrozenie.png')
      ],
      achievements: [
        'Zwiększenie wydajności systemów o 200%',
        'Redukcja czasu przestojów o 95%',
        'Centralizacja zarządzania infrastrukturą'
      ]
    },
    {
      id: 4,
      title: 'System monitoringu sieci',
      category: 'security',
      image: require('./assets/img/monitoring.png'),
      thumbnail: require('./assets/img/monitoring.png'),
      technologies: ['ElasticSearch', 'Kibana', 'Grafana', 'Python', 'Prometheus'],
      description: 'Wdrożenie systemu monitoringu bezpieczeństwa sieci z alertami w czasie rzeczywistym.',
      longDescription: 'Wdrożyliśmy zaawansowany system monitoringu bezpieczeństwa sieci dla firmy telekomunikacyjnej. System zbiera i analizuje logi z urządzeń sieciowych, serwerów i aplikacji, wykrywając anomalie i potencjalne zagrożenia. Rozwiązanie obejmuje mechanizm alertowania w czasie rzeczywistym oraz dashboard z wizualizacją stanu bezpieczeństwa sieci.',
      duration: '4 miesiące',
      completionDate: '2023-05',
      teamSize: 3,
      demoLink: null,
      caseStudyLink: '#',
      gallery: [
        require('./assets/img/monitoring.png')
      ],
      achievements: [
        'Wykrywanie zagrożeń w czasie rzeczywistym',
        'Redukcja czasu reakcji na incydenty z godzin do minut',
        'Pełna widoczność ruchu sieciowego i zachowań użytkowników'
      ]
    },
    {
      id: 5,
      title: 'Modernizacja architektury mikroserwisowej',
      category: 'cloud',
      image: require('./assets/img/modernizacja.png'),
      thumbnail: require('./assets/img/modernizacja.png'),
      technologies: ['Kubernetes', 'Docker', 'AWS', 'CI/CD', 'Microservices'],
      description: 'Transformacja monolitu w architekturę mikroserwisową z wdrożeniem na AWS.',
      longDescription: 'Przeprowadziliśmy transformację monolitycznej aplikacji w architekturę mikroserwisową dla platformy fintech. Rozdzieliliśmy funkcjonalności na niezależne mikroserwisy, wdrożyliśmy orkiestrację za pomocą Kubernetes oraz skonfigurowaliśmy automatyczne pipeline\'y CI/CD. Całość została zmigrowana do AWS z wykorzystaniem usług zarządzanych.',
      duration: '10 miesięcy',
      completionDate: '2023-07',
      teamSize: 8,
      demoLink: null,
      caseStudyLink: '#',
      gallery: [
        require('./assets/img/modernizacja.png')
      ],
      achievements: [
        'Redukcja czasu wdrożenia nowych funkcji o 70%',
        'Zwiększenie skalowalności systemu przy obciążeniu',
        'Obniżenie kosztów infrastruktury o 30%'
      ]
    }
  ];

  // Filter projects based on active category
  const filteredProjects = activeCategory === 'all'
    ? projects
    : projects.filter(project => project.category === activeCategory);

  // Pagination logic
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const currentProjects = filteredProjects.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset page when category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory]);

  // Handle category change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    //setExpandedProject(null); // Collapse any expanded project
  };

  // Open project details modal
  const openProjectDetails = (project) => {
    setSelectedProject(project);
    onOpen();
  };

  // Animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 50, damping: 17 }
    }
  };

  // Map categories to colors
  const categoryColors = {
    'webapps': 'primary',
    'infrastructure': 'success',
    'security': 'danger',
    'cloud': 'secondary',
    'software': 'warning'
  };

  // Gallery image navigation
  const [currentImage, setCurrentImage] = useState(0);

  const nextImage = () => {
    if (!selectedProject) return;
    setCurrentImage((prev) => (prev + 1) % selectedProject.gallery.length);
  };

  const prevImage = () => {
    if (!selectedProject) return;
    setCurrentImage((prev) => 
      prev === 0 ? selectedProject.gallery.length - 1 : prev - 1
    );
  };

  return (
    <div id="portfolio" className="py-20 bg-background relative overflow-hidden" ref={portfolioRef}>
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full filter blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/10 rounded-full filter blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-primary font-medium text-sm tracking-wider uppercase mb-2 inline-block">Nasze prace</span>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Nasze <span className="text-primary">Realizacje</span>
          </h2>
          <p className="text-foreground-muted max-w-2xl mx-auto">
            Poznaj projekty, które zrealizowaliśmy dla naszych klientów. 
            Każda realizacja to historia sukcesu i transformacji biznesowej.
          </p>
        </motion.div>

        {/* Category filters with icons */}
        <motion.div 
          className="flex flex-wrap justify-center gap-3 mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <ButtonGroup variant="flat" className="flex-wrap justify-center">
            {categories.map((category) => (
              <Button
                key={category.key}
                color={activeCategory === category.key ? "primary" : "default"}
                onPress={() => handleCategoryChange(category.key)}
                className={`font-medium ${activeCategory === category.key ? 'shadow-md' : ''}`}
                startContent={category.icon}
                size="sm"
              >
                {category.label}
              </Button>
            ))}
          </ButtonGroup>
        </motion.div>

        {/* Projects grid with animations */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory + currentPage}
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10"
          >
            {currentProjects.map((project) => (
              <motion.div
                key={project.id}
                variants={itemVariants}
                whileHover={{ y: -10, transition: { duration: 0.2 } }}
                layout
              >
                <Card className="shadow-lg overflow-hidden h-full">
                  <CardHeader className="p-0 overflow-hidden">
                    <div className="relative group">
                      <Image
                        src={project.image}
                        alt={project.title}
                        className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110 group-hover:pointer-events-none"
                        removeWrapper
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-10">
                        <div className="px-4 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform group-hover:translate-y-0 translate-y-4 flex gap-2 pointer-events-auto">
                          <Button 
                            color="primary" 
                            variant="solid"
                            size="sm"
                            onPress={() => openProjectDetails(project)}
                            className="backdrop-blur-md bg-primary/90 z-20"
                          >
                            Zobacz szczegóły
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Chip 
                        color={categoryColors[project.category] || "default"} 
                        variant="flat" 
                        size="sm"
                      >
                        {categories.find(c => c.key === project.category)?.label || project.category}
                      </Chip>
                      {project.technologies.slice(0, 2).map((tech, i) => (
                        <Chip key={i} variant="dot" size="sm" className="text-xs">
                          {tech}
                        </Chip>
                      ))}
                      {project.technologies.length > 2 && (
                        <Chip size="sm" variant="bordered" className="text-xs">
                          +{project.technologies.length - 2}
                        </Chip>
                      )}
                    </div>
                    
                    <h3 className="text-xl font-semibold mb-3">
                      {project.title}
                    </h3>
                    <p className="text-foreground-muted text-sm">
                      {project.description}
                    </p>
                  </CardBody>
                  <CardFooter>
                    <Button 
                      color="primary" 
                      variant="flat"
                      size="sm"
                      onPress={() => openProjectDetails(project)}
                      endContent={<FaArrowRight />}
                    >
                      Case study
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center my-8">
            <Pagination 
              total={totalPages}
              initialPage={1}
              page={currentPage}
              onChange={setCurrentPage}
              color="primary"
              showControls
              classNames={{
                cursor: "bg-primary text-white font-medium"
              }}
            />
          </div>
        )}

        {/* Call to Action */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 shadow-lg max-w-3xl mx-auto">
            <CardBody className="py-8">
              <h3 className="text-2xl font-bold mb-3">Masz projekt do realizacji?</h3>
              <p className="text-foreground-muted mb-6 max-w-lg mx-auto">
                Skontaktuj się z nami, aby omówić Twój projekt i dowiedzieć się,
                jak możemy pomóc w jego realizacji.
              </p>
              <Button 
                as="a"
                href="/kontakt"
                color="primary" 
                size="lg"
                className="font-medium"
                endContent={<FaArrowRight />}
              >
                Porozmawiajmy o Twoim projekcie
              </Button>
            </CardBody>
          </Card>
        </motion.div>
      </div>

      {/* Project Details Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="3xl"
        scrollBehavior="inside"
        classNames={{
          backdrop: "backdrop-blur-sm bg-black/50",
          base: "border border-divider"
        }}
        onOpenChange={() => {
          setCurrentImage(0);
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              {selectedProject && (
                <>
                  <ModalHeader className="flex flex-col gap-1">
                    <h3 className="text-2xl">{selectedProject.title}</h3>
                    <div className="flex gap-2 mt-1">
                      <Chip 
                        color={categoryColors[selectedProject.category] || "default"} 
                        variant="flat" 
                        size="sm"
                      >
                        {categories.find(c => c.key === selectedProject.category)?.label || selectedProject.category}
                      </Chip>
                    </div>
                  </ModalHeader>
                  <ModalBody>
                    {/* Image Gallery */}
                    {selectedProject.gallery && selectedProject.gallery.length > 0 && (
                      <div className="relative mb-6 rounded-lg overflow-hidden">
                        <div className="relative aspect-video bg-default-100">
                          <Image
                            src={selectedProject.gallery[currentImage]}
                            alt={`${selectedProject.title} - zdjęcie ${currentImage + 1}`}
                            className="w-full h-full object-cover"
                            removeWrapper
                          />
                        </div>
                        
                        {/* Navigation arrows */}
                        {selectedProject.gallery.length > 1 && (
                          <>
                            <Button
                              isIconOnly
                              variant="flat"
                              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/30 text-white hover:bg-black/50"
                              onClick={prevImage}
                              aria-label="Poprzednie zdjęcie"
                            >
                              <FaChevronLeft />
                            </Button>
                            <Button
                              isIconOnly
                              variant="flat"
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/30 text-white hover:bg-black/50"
                              onClick={nextImage}
                              aria-label="Następne zdjęcie"
                            >
                              <FaChevronRight />
                            </Button>
                            
                            {/* Image counter */}
                            <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs py-1 px-2 rounded-full">
                              {currentImage + 1} / {selectedProject.gallery.length}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    
                    {/* Project details */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="md:col-span-2">
                        <h4 className="text-lg font-semibold mb-3">O projekcie</h4>
                        <p className="text-foreground-muted mb-6">{selectedProject.longDescription}</p>
                        
                        <h4 className="text-lg font-semibold mb-3">Osiągnięcia</h4>
                        <ul className="space-y-2 mb-6">
                          {selectedProject.achievements.map((achievement, idx) => (
                            <li key={idx} className="flex items-start">
                              <span className="text-primary mr-2">•</span>
                              <span>{achievement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div>
                        <Card className="bg-default-50">
                          <CardBody className="space-y-4">                            
                            <div>
                              <h5 className="text-sm text-foreground-muted mb-1">Czas realizacji</h5>
                              <p className="font-medium">{selectedProject.duration}</p>
                            </div>
                            
                            <Divider />
                            
                            <div>
                              <h5 className="text-sm text-foreground-muted mb-1">Zakończenie</h5>
                              <p className="font-medium">
                                {new Date(selectedProject.completionDate).toLocaleDateString('pl-PL', {
                                  year: 'numeric',
                                  month: 'long'
                                })}
                              </p>
                            </div>
                            
                            <Divider />
                            
                            <div>
                              <h5 className="text-sm text-foreground-muted mb-1">Wielkość zespołu</h5>
                              <p className="font-medium">{selectedProject.teamSize} specjalistów</p>
                            </div>
                            
                            <Divider />
                            
                            <div>
                              <h5 className="text-sm text-foreground-muted mb-1">Technologie</h5>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {selectedProject.technologies.map((tech, idx) => (
                                  <Chip key={idx} size="sm" variant="flat" color="primary" className="text-xs">
                                    {tech}
                                  </Chip>
                                ))}
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </div>
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button color="danger" variant="light" onPress={onClose}>
                      Zamknij
                    </Button>
                    <Button 
                      color="primary" 
                      as="a" 
                      href="/kontakt"
                      endContent={<FaArrowRight />}
                    >
                      Zapytaj o podobny projekt
                    </Button>
                  </ModalFooter>
                </>
              )}
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default Portfolio;